import React from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from './auth-context'
import './App.css'
import './Pricing.css'

function Pricing() {
  const { user, loading, currentOrg, organizations } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isWelcome = searchParams.get('welcome') === 'true'

  // Redirect if they came from welcome flow and already have orgs
  React.useEffect(() => {
    if (!loading && user && isWelcome && organizations && organizations.length > 0) {
      navigate('/dashboard')
    }
  }, [user, loading, isWelcome, organizations, navigate])

  // Current plan
  const currentPlan = currentOrg?.subscription_plan_code || null

  // Handle plan selection - redirect to org signup page
  const handlePlanSelection = (planType) => {
    if (!user) {
      // Not logged in - go to organization sign-up (creates owner account + org)
      navigate(`/org-signup?plan=${planType}`)
      return
    }

    // Already logged in - go to org setup (just creates org)
    navigate(`/org-setup?plan=${planType}`)
  }

  const pricingTiers = [
    {
      id: 'standard',
      name: 'Standard',
      description: 'For growing teams',
      price: 250,
      period: 'month',
      features: [
        'Up to 5 team members',
        '10 surveys',
        '1,000 responses per survey',
        'Advanced analytics',
        'CSV export',
        'Priority email support',
        'Team collaboration',
      ],
      disabled: [],
      cta: 'Start Standard Plan',
      onClick: () => handlePlanSelection('standard'),
      popular: true,
    },
    {
      id: 'business',
      name: 'Business',
      description: 'For larger organizations',
      price: 1000,
      period: 'month',
      features: [
        'Up to 20 team members',
        '50 surveys',
        '10,000 responses per survey',
        'Everything in Standard',
        'Custom branding',
        'API access',
        'Dedicated support',
      ],
      disabled: [],
      cta: 'Start Business Plan',
      onClick: () => handlePlanSelection('business'),
      popular: false,
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'Custom solutions',
      price: null,
      period: 'custom',
      features: [
        'Unlimited team members',
        'Unlimited surveys',
        'Unlimited responses',
        'Everything in Business',
        'SSO & advanced security',
        'Audit logs',
        '24/7 priority support',
      ],
      disabled: [],
      cta: 'Contact Sales',
      onClick: () => handlePlanSelection('enterprise'),
      popular: false,
      enterprise: true,
    },
  ]

  return (
    <div className="pricing-page">
      <header className="pricing-header">
        {isWelcome && (
          <div
            style={{
              background: 'rgba(99, 102, 241, 0.1)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              padding: '1rem 2rem',
              borderRadius: '12px',
              marginBottom: '2rem',
              color: 'rgba(45, 55, 72, 100)',
            }}>
            <strong>🎉 Welcome onboard!</strong> Choose the plan that's right for your team.
          </div>
        )}
        <h1>Choose Your Plan</h1>
        <p>Scale your surveys with flexible pricing that grows with your needs</p>
      </header>

      <div className="pricing-grid">
        {pricingTiers.map((tier) => (
          <div
            key={tier.id}
            className={`pricing-card ${tier.popular ? 'popular' : ''} ${
              tier.enterprise ? 'enterprise' : ''
            }`}>
            {tier.popular && <div className="popular-badge">Most Popular</div>}

            <div className="pricing-card-header">
              <h2 className="pricing-tier">{tier.name}</h2>
              <p className="pricing-description">{tier.description}</p>
            </div>

            <div className="pricing-price">
              {tier.price !== null ? (
                <>
                  <div className="price-amount">
                    <span className="currency">₹</span>
                    <span className="amount">{tier.price}</span>
                    <span className="period">/{tier.period}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="price-amount">
                    <span className="amount" style={{ fontSize: '2.5rem' }}>
                      Custom
                    </span>
                  </div>
                  <p className="price-subtitle">Tailored to your needs</p>
                </>
              )}
            </div>

            <ul className="pricing-features">
              {tier.features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
              {tier.disabled.map((feature, index) => (
                <li key={`disabled-${index}`} className="disabled">
                  {feature}
                </li>
              ))}
            </ul>

            {currentPlan === tier.id ? (
              <button className="pricing-cta current" onClick={() => navigate('/dashboard')}>
                ✓ Go to Dashboard
              </button>
            ) : (
              <button
                className={`pricing-cta ${tier.popular ? 'primary' : 'secondary'}`}
                onClick={tier.onClick}>
                {tier.cta}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default Pricing
